/**
 * Created by Mikhail Menshenin on 24.04.2024
 */

const LevelMastery = function (mission, isNewMission) {
    cleverapps.EventEmitter.call(this);
    this.mission = mission;
    const data = this.mission && this.mission.details || {};
    this.level = data && data.level || 0;
    this.stage = data && data.stage || 0;
    this.stages = this.mission.stages;
    this.countDown = new cleverapps.CountDown(this.getTimeLeft(), {
        permanent: true,
        onFinish: () => this.trigger("timeout")
    });

    if (this.stage >= this.stages.length) {
        this.mission.complete();
    }

    if (isNewMission) {
        cleverapps.missionManager.trigger("addLevelMasteryBar");
    }

    if (this.mission.isRunning()) {
        this.mission.logEvent(Mission.EVENTS.MISSION_STAGE, { itemId: `${this.stage}`, once: true });
    }
};

LevelMastery.prototype = Object.create(cleverapps.EventEmitter.prototype);
LevelMastery.prototype.constructor = LevelMastery;

LevelMastery.prototype.getView = function () {
    if (cleverapps.config.ui === "riddles") {
        return RiddlesLevelMasteryWindow;
    }

    return this.mission.view;
};

LevelMastery.prototype.getTimeLeft = function () {
    return this.mission.getTimeLeft();
};

LevelMastery.prototype.isAttentionRequired = function () {
    return this.isStageCompleted();
};

LevelMastery.prototype.isCompleted = function (stage) {
    return stage < this.stage;
};

LevelMastery.prototype.isCurrent = function (stage) {
    return stage === this.stage;
};

LevelMastery.prototype.isLastStage = function (stage) {
    return stage === this.stages.length - 1;
};

LevelMastery.prototype.getStageRewards = function () {
    return this.stages.map((stage) => this.getStageReward(stage));
};

LevelMastery.prototype.getCurrentReward = function () {
    const stage = this.getCurrentStage();

    return this.getStageReward(stage);
};

LevelMastery.prototype.getStageReward = function (stage) {
    return new RewardsList(stage.prize, {
        event: cleverapps.EVENTS.EARN.LEVELMASTERY,
        toBubbleStack: true
    });
};

LevelMastery.prototype.getCurrentStage = function () {
    return this.getStage(this.stage);
};

LevelMastery.prototype.getStage = function (stage) {
    return this.stages[stage];
};

LevelMastery.prototype.getMaxStage = function () {
    return this.stages.length - 1;
};

LevelMastery.prototype.setLevel = function (level) {
    if (this.level !== level) {
        this.level = level;
        this.trigger("updateLevel");
    }
};

LevelMastery.prototype.updateStage = function () {
    this.level -= this.getCurrentStage().goal;
    this.stage += 1;
    this.mission.logEvent(Mission.EVENTS.MISSION_STAGE, { itemId: `${this.stage}` });

    if (this.stage >= this.stages.length) {
        this.mission.logEvent(Mission.EVENTS.MISSION_FINISH, { once: true });
        this.mission.complete();
    } else {
        this.trigger("updateStage");
        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.LEVEL_MASTERY_STAGE}${this.stage}`);
    }

    this.update();
};

LevelMastery.prototype.isStageCompleted = function () {
    const stage = this.getCurrentStage();
    if (!stage) {
        return false;
    }

    return this.level >= stage.goal;
};

LevelMastery.prototype.gamePlayed = function (game) {
    if (game.outcome === GameBase.OUTCOME_VICTORY) {
        this.level += 1;
        this.pendingDelta = (this.pendingDelta || 0) + 1;
        this.mission.logEvent(Mission.EVENTS.MISSION_POINTS, { value: 1 });
        this.mission.logEvent(Mission.EVENTS.MISSION_ACTION, { once: true });
    } else if (game.outcome === GameBase.OUTCOME_LOST) {
        this.level = 0;
        delete this.pendingDelta;
    }

    this.update();
};

LevelMastery.prototype.getShownLevel = function () {
    return this.level - (this.pendingDelta || 0);
};

LevelMastery.prototype.receiveRewards = function (f) {
    if (!this.pendingDelta) {
        f();
        return;
    }

    delete this.pendingDelta;

    cleverapps.focusManager.showControlsWhileFocused("levelmastery_progress");
    this.trigger("updateLevel");
    f();
};

LevelMastery.prototype.incLevel = function (stars) {
    this.level += stars;
    this.mission.logEvent(Mission.EVENTS.MISSION_POINTS, { value: stars });
    this.mission.logEvent(Mission.EVENTS.MISSION_ACTION, { once: true });
    this.trigger("updateLevel", true, stars);
    this.update();
};

LevelMastery.prototype.getInfo = function () {
    return {
        level: this.level,
        stage: this.stage
    };
};

LevelMastery.prototype.save = function (details) {
    details.level = this.level;
    details.stage = this.stage;
};

LevelMastery.prototype.update = function () {
    this.mission.update(0, this.getInfo());
    this.updateIcon();
};

LevelMastery.prototype.skipNext = function () {
    return this.isStageCompleted();
};

LevelMastery.prototype.updateIcon = function () {
    this.mission.updateMissionIcon();
    this.trigger("updateStatus");
};

LevelMastery.prototype.onPressed = function () {
    if (this.isStageCompleted()) {
        cleverapps.missionManager.trigger("clickReceiveButton", this.mission.getType());
        return;
    }

    cleverapps.focusManager.display({
        focus: `${this.mission.name}MissionIconClicked`,
        action: function (f) {
            const ViewClass = this.mission.getViewClass();
            new ViewClass(this.mission);
            cleverapps.focusManager.onceNoWindowsListener = f;
        }.bind(this)
    });
};

LevelMastery.prototype.calcStatusInfo = function () {
    const currentStage = this.getCurrentStage();
    if (!currentStage) {
        return;
    }

    const amount = this.level;
    const target = currentStage.goal;

    return {
        text: `%% ${amount}/${target}`,
        icons: {
            "%%": bundles.reward_icons.frames.order_completion_star_png
        },
        progressBar: { amount, target },
        prize: this.isStageCompleted()
    };
};

LevelMastery.prototype.receiveReward = function () {
    if (!this.isStageCompleted()) {
        return;
    }

    let rewards = {};
    while (this.isStageCompleted()) {
        rewards = RewardsList.sumRewards(rewards, this.getCurrentStage().prize);
        this.updateStage();
    }

    return new RewardsList(rewards, {
        event: cleverapps.EVENTS.EARN.LEVELMASTERY,
        toBubbleStack: true
    });
};

LevelMastery.prototype.canRemove = () => true;
LevelMastery.prototype.canRemoveSilently = function () {
    return !this.isStageCompleted();
};
